'use client'

import { useState, useMemo, useEffect } from 'react'
import Image from 'next/image'
import { ArrowLeft, Filter, Loader2 } from 'lucide-react'
import { Movie, Theatre, UserLocation } from '@/types'
import { getOptimizedImageUrl } from '@/lib/utils'
import { parseShowtimeAttributes, getFormatPriority } from '@/lib/amc-api'
import ShowtimesCollapsible from './showtimes-collapsible'

interface MovieDetailProps {
  movie: Movie
  onBack: () => void
  userCategory?: string
  isAuthenticated?: boolean
  onCategoryChange: (movieId: string, category: string) => void
  onRequestLogin: () => void
  theaterNames?: Map<string, string>
  theaters?: Theatre[]
  userLocation?: UserLocation | null
  scheduledShowtimesByTheater?: Map<string, any[]>
  isLoadingScheduledShowtimes?: boolean
  initialDate?: string | null
  initialFormat?: string | null
  initialLanguage?: string | null
  initialTheater?: string | null
}

const EMPTY_ARRAY: Theatre[] = []
const EMPTY_MAP = new Map<string, any>()

export default function MovieDetail({
  movie,
  onBack,
  userCategory,
  isAuthenticated,
  onCategoryChange,
  onRequestLogin,
  theaterNames = EMPTY_MAP,
  theaters = EMPTY_ARRAY,
  userLocation,
  scheduledShowtimesByTheater = EMPTY_MAP,
  isLoadingScheduledShowtimes = false,
  initialDate = '',
  initialFormat = '',
  initialLanguage = '',
  initialTheater = '',
}: MovieDetailProps) {
  // Convert 'YYYY-MM-DD' from homepage to matching local date string format
  const formattedInitialDate = useMemo(() => {
    if (!initialDate) return ''
    const parts = initialDate.split('-')
    if (parts.length !== 3) return ''
    const [year, month, day] = parts.map(Number)
    return new Date(year, month - 1, day).toLocaleDateString()
  }, [initialDate])

  const [selectedDate, setSelectedDate] = useState<string>(formattedInitialDate || '')
  const [selectedFormat, setSelectedFormat] = useState<string>(initialFormat || '')
  const [selectedLanguage, setSelectedLanguage] = useState<string>(initialLanguage || '')
  const [selectedTheater, setSelectedTheater] = useState<string>(
    initialTheater ? (theaterNames.get(String(initialTheater)) || '') : ''
  )
  const [showFilters, setShowFilters] = useState(
    !!(initialDate || initialFormat || initialLanguage || initialTheater)
  )

  // Merge today's showtimes and future scheduled showtimes for this movie
  const allShowtimes = useMemo(() => {
    const merged = [...movie.showtimes]
    const existingShowIds = new Set(movie.showtimes.map(s => String(s.showId)))

    let mergedCount = 0
    let skippedCount = 0
    let mismatchCount = 0

    scheduledShowtimesByTheater.forEach((theatreShowtimes, theaterId) => {
      theatreShowtimes.forEach((scheduled: any) => {
        const schedMovieIdStr = String(scheduled.movieId)
        const movieMovieIdStr = String(movie.movieId)

        if (schedMovieIdStr === movieMovieIdStr) {
          const showId = String(scheduled.showId || scheduled.id)
          const isDuplicate = existingShowIds.has(showId)
          const dateTimeLocal = scheduled.showDateTimeLocal || scheduled.showDateTimeUtc || ''
          
          if (!isDuplicate) {
            if (!dateTimeLocal) {
              return
            }

            merged.push({
              showId: showId,
              movieId: String(scheduled.movieId),
              theatreId: String(scheduled.theatreId),
              theatreName: theaterNames.get(String(scheduled.theatreId)) || 'Unknown Theater',
              showDateTimeUtc: scheduled.showDateTimeUtc,
              showDateTimeLocal: dateTimeLocal,
              attributes: scheduled.attributes || [],
              purchaseUrl: scheduled.purchaseUrl || scheduled.ticketingUrl || '',
              movieName: scheduled.movieName || movie.movieName,
              isSoldOut: scheduled.isSoldOut || false,
              isCanceled: scheduled.isCanceled || false,
            })
            existingShowIds.add(showId)
            mergedCount++
          } else {
            skippedCount++
          }
        } else {
          mismatchCount++
        }
      })
    })

    return merged
  }, [movie, scheduledShowtimesByTheater, theaterNames])

  // Extract unique values for filters from the merged showtimes list
  const filters = useMemo(() => {
    const dates = new Set<string>()
    const formats = new Set<string>()
    const theaters = new Set<string>()
    const languages = new Set<string>()

    allShowtimes.forEach(showtime => {
      const date = new Date(showtime.showDateTimeLocal).toLocaleDateString()
      dates.add(date)

      const parsed = parseShowtimeAttributes(showtime.attributes)
      if (parsed.format) {
        formats.add(parsed.format)
      }
      if (parsed.language) {
        languages.add(parsed.language)
      }

      if (showtime.theatreName) {
        theaters.add(showtime.theatreName)
      }
    })

    return {
      dates: Array.from(dates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
      formats: Array.from(formats).sort((a, b) => {
        const priorityA = getFormatPriority(a)
        const priorityB = getFormatPriority(b)
        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }
        return a.localeCompare(b)
      }),
      theaters: Array.from(theaters).sort(),
      languages: Array.from(languages).sort(),
    }
  }, [allShowtimes])

  // Filter showtimes based on selections from the merged list
  const filteredShowtimes = useMemo(() => {
    const result = allShowtimes.filter(showtime => {
      const showDate = new Date(showtime.showDateTimeLocal).toLocaleDateString()
      
      if (selectedDate && showDate !== selectedDate) return false
      
      if (selectedFormat) {
        const parsed = parseShowtimeAttributes(showtime.attributes)
        if (parsed.format !== selectedFormat) return false
      }

      if (selectedLanguage) {
        const parsed = parseShowtimeAttributes(showtime.attributes)
        if (parsed.language !== selectedLanguage) return false
      }

      if (selectedTheater && showtime.theatreName !== selectedTheater) return false

      // Past showtimes are allowed to be shown in the UI
      return true
    })

    return result
  }, [allShowtimes, selectedDate, selectedFormat, selectedLanguage, selectedTheater])

  // Group by theater
  const showtimesByTheater = useMemo(() => {
    const grouped: Record<string, any[]> = {}
    filteredShowtimes.forEach(showtime => {
      const theater = showtime.theatreName || 'Unknown Theater'
      if (!grouped[theater]) {
        grouped[theater] = []
      }
      grouped[theater].push(showtime)
    })
    return grouped
  }, [filteredShowtimes])

  const imdbUrl = `https://www.imdb.com/find/?q=${encodeURIComponent(movie.movieName)}`

  return (
    <div className="space-y-4">
      {/* Movie Header */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Poster */}
          <div className="flex justify-center sm:justify-start">
            <div className="relative w-32 h-48 rounded-lg overflow-hidden shadow-md flex-shrink-0">
              <Image
                src={getOptimizedImageUrl(movie.posterUrl) || '/placeholder-movie.jpg'}
                alt={movie.movieName}
                fill
                className="object-cover"
              />
            </div>
          </div>

          {/* Movie Info */}
          <div className="sm:col-span-2 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{movie.movieName}</h1>
                <p className="text-sm text-slate-600 mt-1">
                  {movie.genre && <span>{movie.genre}</span>}
                  {movie.mpaaRating && <span> • {movie.mpaaRating}</span>}
                  {movie.runTime && <span> • {movie.runTime} min</span>}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={imdbUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
                >
                  IMDB
                </a>
                <button
                  type="button"
                  onClick={() => setShowFilters(prev => !prev)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
                >
                  <Filter size={16} />
                  {showFilters ? 'Hide filters' : 'Show filters'}
                </button>
              </div>
            </div>

            {/* Category Selector */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-slate-700">Your Status:</label>
              {isAuthenticated ? (
                <div className="flex flex-wrap gap-1">
                  {['starred', 'watched', 'watch-again', 'ignored'].map(cat => (
                    <button
                      key={cat}
                      onClick={() => onCategoryChange(movie.movieId, cat)}
                      className={`px-2 py-1 rounded text-xs font-medium transition ${
                        userCategory === cat
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {cat
                        .split('-')
                        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded text-xs p-2">
                  <p className="text-xs text-blue-800">
                    <button
                      type="button"
                      onClick={onRequestLogin}
                      className="font-semibold underline hover:no-underline"
                    >
                      Sign in
                    </button>
                    {' '}to track movies.
                  </p>
                </div>
              )}
            </div>

            {movie.availableForAList === false && (
              <div className="bg-orange-50 border border-orange-200 rounded text-xs p-2">
                <p className="text-xs text-orange-800">
                  ⚠️ Not available with A-List
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Filters</h2>
            <button
              type="button"
              onClick={() => setShowFilters(false)}
              className="text-slate-500 hover:text-slate-700 text-xs"
            >
              Close
            </button>
          </div>

          <div className={`grid gap-2 ${filters.languages && filters.languages.length > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {/* Date Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Date</label>
            <select
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value="">All</option>
              {filters.dates.map(date => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>

          {/* Format Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Format</label>
            <select
              value={selectedFormat}
              onChange={e => setSelectedFormat(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value="">All</option>
              {filters.formats.map(format => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
          </div>

          {/* Language Filter */}
          {filters.languages && filters.languages.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Language</label>
              <select
                value={selectedLanguage}
                onChange={e => setSelectedLanguage(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500"
              >
                <option value="">All</option>
                {filters.languages.map(lang => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Theater Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Theater</label>
            <select
              value={selectedTheater}
              onChange={e => setSelectedTheater(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value="">All</option>
              {filters.theaters.map(theater => (
                <option key={theater} value={theater}>
                  {theater}
                </option>
              ))}
            </select>
          </div>
        </div>
        </div>
      )}

      {/* Showtimes */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-sm font-semibold text-slate-900">
            Showtimes
          </h2>
          {isLoadingScheduledShowtimes && (
            <span className="flex items-center text-xs text-slate-500 gap-1 animate-pulse">
              <Loader2 className="animate-spin" size={12} />
              Loading future dates...
            </span>
          )}
        </div>
        <ShowtimesCollapsible 
          showtimes={filteredShowtimes} 
          theaterNames={theaterNames} 
          theaters={theaters}
          userLocation={userLocation}
        />
      </div>
    </div>
  )
}
